import { makeAutoObservable, runInAction } from 'mobx'
import { showToast } from 'shared/ui'
import { logger } from 'shared/lib'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { UsersApi } from 'entities/Users/api/users'
import type { IParamsSetOptionsNotifications } from 'entities/Users/api/types'
import {
  ANALYTICS_FREQUENCY_ITEMS,
  ANALYTICS_REPORT_FEATURE_KEY,
  ANALYTICS_REPORT_PARAM_TITLE,
  FREQUENCY_KEYS,
  type IAnalyticsFrequencyKey,
  NOTIFICATIONS_FEATURE_KEY,
  NOTIFICATIONS_PARAM_TITLE,
} from 'pages/settings/pages/notifications/constants/constants'

export type IAnalyticsReportSettings = {
  active: boolean
  daily: boolean
  weekly: boolean
  monthly: boolean
}

export type IAnalyticsToast = 'toggle' | 'frequencyUpdate'

const emptyAnalyticsReport: IAnalyticsReportSettings = {
  active: false,
  daily: false,
  weekly: false,
  monthly: false,
}

export class NotificationsStore {
  initialLoading = true
  ringEverywhereLoading = false
  optionsLoading = false
  analyticsReportSelectedValues: IAnalyticsFrequencyKey[] = []
  private _hideAnalyticsSelect = false

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
    this.init()
  }

  init = async () => {
    await Promise.all([
      this._entities.usersStore.initPromise,
      this._entities.userSettingsStore.initPromise,
    ])
    runInAction(() => {
      this.syncAnalyticsReportSelectedValues()
      this.initialLoading = false
    })
  }

  get ringEverywhere() {
    return this._entities.usersStore.isRingEverywhere
  }

  get notificationsEmail() {
    const item = this._entities.userSettingsStore.getItem(NOTIFICATIONS_FEATURE_KEY)
    const value = item?.settings?.email
    return Boolean(value)
  }

  get analyticsReport() {
    const item = this._entities.userSettingsStore.getItem(ANALYTICS_REPORT_FEATURE_KEY)
    if (!item?.settings) return emptyAnalyticsReport
    const report = !item?.settings
      ? emptyAnalyticsReport
      : (item.settings as IAnalyticsReportSettings)
    return report
  }

  get isShowAnalyticsSelect() {
    return this.analyticsReport.active && !this._hideAnalyticsSelect
  }

  get analyticsReportSelectionValues() {
    return this.analyticsReportSelectedValues
  }

  get hasAnyAnalyticsReportFrequency() {
    return FREQUENCY_KEYS.some((key) => this.analyticsReport[key])
  }

  get analyticsFrequencyText() {
    return (
      this.analyticsReportSelectionValues
        .map((id) => ANALYTICS_FREQUENCY_ITEMS.find((item) => item.id === id)?.label)
        .join(', ') || 'Select'
    )
  }

  get isMemberOrObserver() {
    return (
      this._entities.usersStore.user?.isMemberRole || this._entities.usersStore.user?.isViewOnlyRole
    )
  }

  syncAnalyticsReportSelectedValues = () => {
    this.analyticsReportSelectedValues = FREQUENCY_KEYS.filter((key) => this.analyticsReport[key])
  }

  setAnalyticsReportSelectedValues = (values: IAnalyticsFrequencyKey[]) => {
    this.analyticsReportSelectedValues = FREQUENCY_KEYS.filter((key) => values.includes(key))
  }

  closeAnalyticsReportDropdown = async () => {
    const actualSelectedValues = this.analyticsReportSelectedValues
    if (!actualSelectedValues.length) {
      this._hideAnalyticsSelect = true
    }
    const saved = FREQUENCY_KEYS.filter((key) => this.analyticsReport[key])
    const isSame =
      actualSelectedValues.length === saved.length &&
      actualSelectedValues.every((id) => saved.includes(id))

    if (!isSame) {
      try {
        await this.setAnalyticsReportFrequencies(actualSelectedValues)
        this._hideAnalyticsSelect = false
      } catch (e) {
        if (!actualSelectedValues.length) {
          this._hideAnalyticsSelect = false
        }
      }
    }
  }

  toggleRingEverywhere = async () => {
    try {
      runInAction(() => {
        this.ringEverywhereLoading = true
      })
      const { data } = await UsersApi.updateUsersToggleRingEverywhere({
        isRingEverywhere: !this.ringEverywhere,
      })
      runInAction(() => {
        this._entities.usersStore.isRingEverywhere = data.isRingEverywhere
      })
      showToast({
        type: 'success',
        title: `Ring everywhere ${data.isRingEverywhere ? 'enabled' : 'disabled'}`,
      })
    } catch (e) {
      logger.error(e)
      showToast({
        type: 'error',
        title: 'Failed to update ring everywhere',
      })
    } finally {
      runInAction(() => {
        this.ringEverywhereLoading = false
      })
    }
  }

  private _getSuccessToastTitleFromParams = (
    params: IParamsSetOptionsNotifications,
    analyticsToast?: IAnalyticsToast
  ) => {
    const email = params[NOTIFICATIONS_PARAM_TITLE]
    const analyticsActive = params[ANALYTICS_REPORT_PARAM_TITLE]

    if (email) {
      return `Email notifications ${email.value ? 'enabled' : 'disabled'}`
    }

    const activeValue = analyticsActive?.value
    const analyticsTitles = {
      enabled: 'Activity report via email enabled',
      disabled: 'Activity report via email disabled',
      updated: 'Activity report via email updated',
    }

    if (analyticsToast === 'toggle') {
      return analyticsTitles[activeValue ? 'enabled' : 'disabled']
    }
    if (analyticsToast === 'frequencyUpdate') {
      return activeValue === false ? analyticsTitles.disabled : analyticsTitles.updated
    }

    return 'Notifications updated'
  }

  setOptionsNotifications = async (
    params: IParamsSetOptionsNotifications,
    analyticsToast?: IAnalyticsToast
  ) => {
    try {
      runInAction(() => {
        this.optionsLoading = true
      })
      await UsersApi.setOptionsNotifications(params)
      await this._entities.userSettingsStore.fetchUsersSettings()
      showToast({
        type: 'success',
        title: this._getSuccessToastTitleFromParams(params, analyticsToast),
      })
      runInAction(() => {
        this.syncAnalyticsReportSelectedValues()
      })
    } catch (e) {
      logger.error(e)
      showToast({
        type: 'error',
        title: 'Failed to update notifications',
      })
    } finally {
      runInAction(() => {
        this.optionsLoading = false
      })
    }
  }

  toggleNotificationsEmail = async () => {
    await this.setOptionsNotifications({
      [NOTIFICATIONS_PARAM_TITLE]: {
        featureKey: NOTIFICATIONS_FEATURE_KEY,
        settingsKey: 'email',
        value: !this.notificationsEmail,
      },
    })
  }

  toggleAnalyticsReportActive = async () => {
    const payload = {
      [ANALYTICS_REPORT_PARAM_TITLE]: {
        featureKey: ANALYTICS_REPORT_FEATURE_KEY,
        settingsKey: 'active',
        value: !this.analyticsReport.active,
      },
    }

    if (!this.analyticsReport.active && !this.hasAnyAnalyticsReportFrequency) {
      await this._setAnalyticsReportActiveAndFrequencies(
        true,
        {
          daily: true,
          weekly: true,
          monthly: true,
        },
        'toggle'
      )
    } else {
      await this.setOptionsNotifications(payload, 'toggle')
    }
  }

  setAnalyticsReportFrequencies = async (values: string[]) => {
    const frequencies = FREQUENCY_KEYS.reduce((acc, key) => {
      acc[key] = values.includes(key)
      return acc
    }, {} as Record<IAnalyticsFrequencyKey, boolean>)
    const hasSelection = values.length > 0
    await this._setAnalyticsReportActiveAndFrequencies(hasSelection, frequencies, 'frequencyUpdate')
  }

  private _setAnalyticsReportActiveAndFrequencies = async (
    active: boolean,
    frequencies: Record<IAnalyticsFrequencyKey, boolean>,
    analyticsToast?: IAnalyticsToast
  ) => {
    const payload: IParamsSetOptionsNotifications = {
      [ANALYTICS_REPORT_PARAM_TITLE]: {
        featureKey: ANALYTICS_REPORT_FEATURE_KEY,
        settingsKey: 'active',
        value: active,
      },
      [`${ANALYTICS_REPORT_FEATURE_KEY}.daily`]: {
        featureKey: ANALYTICS_REPORT_FEATURE_KEY,
        settingsKey: 'daily',
        value: frequencies.daily,
      },
      [`${ANALYTICS_REPORT_FEATURE_KEY}.weekly`]: {
        featureKey: ANALYTICS_REPORT_FEATURE_KEY,
        settingsKey: 'weekly',
        value: frequencies.weekly,
      },
      [`${ANALYTICS_REPORT_FEATURE_KEY}.monthly`]: {
        featureKey: ANALYTICS_REPORT_FEATURE_KEY,
        settingsKey: 'monthly',
        value: frequencies.monthly,
      },
    }
    await this.setOptionsNotifications(payload, analyticsToast)
  }
}
